<script setup>
import {
  ref, computed, watch, onMounted, inject, useAttrs,
} from 'vue';
import { debounce, uniqBy } from 'lodash';
import usuarios from '@/api/usuarios';
import SelectCifra from '@/components/inputs/SelectCifra.vue';

const props = defineProps({
  modelValue: { type: [Number, String, Array], default: undefined },
  marcador: {
    type: String,
    default: undefined,
  },
  multiple: { type: Boolean, default: false },
  fullNameFormat: { type: Boolean, default: true },
  icon: { type: Boolean, default: false },
  showLabels: { type: Boolean, default: false },
  tipo: { type: String, default: null },
  activos: { type: Boolean, default: null },
  precargar: { type: Boolean, default: false },
  usuariosFijos: { type: Array, default: null },
  busquedaInterna: { type: Boolean, default: true },
  ocultarSeleccionados: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue', 'usuarios-preparados']);

const attrs = useAttrs();
const $I18n = inject('I18n');

const localValue = ref(null);
const loading = ref(false);
const omitirSiguiente = ref(false);

const marcadorFinal = computed(() => {
  if (props.marcador !== undefined) return props.marcador;
  if (typeof attrs.marcador === 'string') return attrs.marcador;
  if (typeof attrs.placeholder === 'string') return attrs.placeholder;
  return $I18n.t('introduzca_usuario');
});

const valoresGrupo = computed(() => attrs['valores-grupo'] || attrs['group-values']);
const etiquetaGrupo = computed(() => attrs['etiqueta-grupo'] || attrs['group-label']);
const seleccionarGrupo = computed(() => (
  attrs['seleccionar-grupo'] ?? attrs['group-select'] ?? false
));
const deshabilitado = computed(() => attrs.deshabilitado ?? attrs.disabled ?? false);
const nombreInput = computed(() => attrs['nombre-input'] || attrs['input-name']);

const usuarioActual = computed(() => localValue.value);

function usuarioIcono(usuario) {
  let icono = 'fa fa-user fa-blank';
  let title = '';
  if (usuario.caracteristicas.alumno) {
    icono = 'fa fa-user text-alumno';
    title = $I18n.t('Alumno');
  } else if (usuario.caracteristicas.profesor) {
    icono = 'fa fa-user-graduate text-profesor';
    title = $I18n.t('Profesor');
  } else if (usuario.caracteristicas.padre) {
    icono = 'fa fa-user-tie text-padre';
    title = $I18n.t('Padre');
  }
  return { icono, title };
}

function prepararUsuarios(users) {
  let usuariosFiltrados = users.filter((user) => user);
  usuariosFiltrados = uniqBy(usuariosFiltrados, 'id');
  usuariosFiltrados.forEach((usuario) => {
    usuario.fullname = usuarios.fullname(usuario, props.fullNameFormat);
    if (props.icon) {
      usuario.icon = usuarioIcono(usuario);
    }
  });
  emit('usuarios-preparados', usuariosFiltrados);
  return usuariosFiltrados;
}

function prepararUsuariosIniciales() {
  if (!props.usuariosFijos) {
    return [];
  }

  let resultado;
  const nombreGrupo = valoresGrupo.value;
  if (nombreGrupo) {
    resultado = props.usuariosFijos.map((grupo) => {
      grupo[nombreGrupo] = prepararUsuarios(grupo[nombreGrupo]);
      return grupo;
    });
  } else {
    resultado = prepararUsuarios(props.usuariosFijos);
  }
  return resultado;
}

const usuariosLista = ref(prepararUsuariosIniciales());

const ajaxSearch = debounce((texto) => {
  const params = { q: texto };
  if (props.tipo) {
    params[props.tipo] = true;
  }
  if (props.activos !== null) {
    params.activo = props.activos;
  }
  usuarios.list({ params }).then((result) => {
    usuariosLista.value = prepararUsuarios(result.data);
    loading.value = false;
  }).catch((_result) => {
    loading.value = false;
  });
}, 350);

function onSearch(texto) {
  if (props.usuariosFijos) {
    return;
  }
  if (texto.length) {
    if (!props.precargar) {
      loading.value = true;
      ajaxSearch(texto);
    }
  }
}

function getOptionLabel(opcion) {
  if (!opcion) return '';
  if (opcion.isTag) return opcion.label;
  if (opcion.$isLabel) return opcion.$groupLabel;

  return opcion.fullname;
}

function etiquetaUsuario(opcion, _campoEtiqueta) {
  return getOptionLabel(opcion);
}

function cargarUsuariosById(usuarioIds) {
  if (!usuarioIds) {
    return;
  }
  usuarios.list({ params: { id: usuarioIds } }).then((result) => {
    const lista = prepararUsuarios(result.data);
    if (props.multiple) {
      localValue.value = lista;
    } else {
      localValue.value = lista.length > 0 ? lista[0] : null;
    }
  });
}

function addUsuarios(users) {
  localValue.value = localValue.value || [];
  localValue.value = prepararUsuarios(localValue.value.concat(users));
}

watch(localValue, (value) => {
  let nuevo = value;
  if (Array.isArray(nuevo)) {
    nuevo = value.map((val) => val.id);
  } else if (value instanceof Object) {
    nuevo = nuevo.id;
  }

  omitirSiguiente.value = true;
  emit('update:modelValue', nuevo);
});

watch(() => props.modelValue, (value) => {
  if (omitirSiguiente.value) {
    omitirSiguiente.value = false;
    return;
  }

  cargarUsuariosById(value);
});

onMounted(() => {
  omitirSiguiente.value = false;
  if (props.precargar) {
    ajaxSearch(null);
  } else {
    cargarUsuariosById(props.modelValue);
  }
});

defineExpose({
  addUsuarios,
  usuarioActual,
});
</script>

<template>
  <SelectCifra
    v-model="localValue"
    :opciones="usuariosLista"
    :cargando="loading"
    :marcador="marcadorFinal"
    :multiple="multiple"
    :mostrar-etiquetas="showLabels"
    :busqueda-interna="busquedaInterna"
    :ocultar-seleccionados="ocultarSeleccionados"
    campo-etiqueta="fullname"
    campo-id="id"
    :valores-grupo="valoresGrupo"
    :etiqueta-grupo="etiquetaGrupo"
    :seleccionar-grupo="seleccionarGrupo"
    :deshabilitado="deshabilitado"
    :nombre-input="nombreInput"
    :etiqueta-personalizada="etiquetaUsuario"
    @cambio-busqueda="onSearch"
  >
    <template #opcion="{ opcion }">
      <span
        v-if="icon"
        :class="opcion.icon.icono"
        data-toggle="tooltip"
        data-placement="left"
        :title="opcion.icon.title"
      ></span>
      {{ getOptionLabel(opcion) }}
      <span
        v-if="opcion.contador"
        class="label label-info pull-right"
        data-toggle="tooltip"
        :title="opcion.tooltipContador"
      >
        {{ opcion.contador }}
      </span>
    </template>
  </SelectCifra>
</template>

<style scoped>
.select-cifra__opcion .fa {
  margin-right: 5px;
}
</style>
